import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  ClipboardList, Search, ChevronDown, ChevronRight,
  CheckCircle, XCircle, Clock, Users, Award, Code2, Copy,
  ListChecks, FileText, Tag, Layers, Calendar, Timer
} from "lucide-react";
import Editor from "@monaco-editor/react";
import apiClient from "../../utils/apiClient";
import DataTable from "../Common/DataTable";
import {
  PageHeader, AppBadge, Tabs, TabsList, TabsTrigger, TabsContent,
  FilterButton, ActiveFilterChips, FilterPanel, FilterSelectField, useListFilters,
} from "../ui";
import { getCurrentUser } from "../../utils/auth";
import { formatToReadableDateTime } from "../../utils/common";
import "./Assignments.scss";

const SESSION_STATUSES = ["", "pending", "active", "submitted", "expired"];

const statusStyle = {
  pending: { variant: "secondary", label: "Pending" },
  active: { variant: "info", label: "In Progress" },
  submitted: { variant: "success", label: "Submitted" },
  expired: { variant: "danger", label: "Expired" },
};

const difficultyClass = (d) =>
  d === "Hard" ? "asd-diff--hard" : d === "Medium" ? "asd-diff--medium" : "asd-diff--easy";

const AssignmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const prefix = getCurrentUser()?.usertype === "instructor" ? "/i" : "";

  const [assignment, setAssignment] = useState(null);
  const [detail, setDetail] = useState(null); // full assignment w/ questions + description
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("questions");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  /* The FilterPanel pattern (D3), replacing an inline strip plus a mobile-only
     modal that rendered the same single filter twice. `empty: ""` — this API
     reads the empty string, not the "all" sentinel. */
  const listFilters = useListFilters({
    status: {
      label: "Status", empty: "",
      options: [
        { value: "", label: "All Statuses" },
        ...SESSION_STATUSES.filter(Boolean).map((st) => ({ value: st, label: statusStyle[st]?.label || st })),
      ],
    },
  });
  /* ⚠️ Memoised: this is a useEffect dependency, and a bare literal would loop. */
  const filterValues = listFilters.values;
  const filters = useMemo(() => ({ ...filterValues, search }), [filterValues, search]);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/assignments/${id}/results`, {
        params: { ...filters, page, limit: pageSize },
      });
      setAssignment(data.assignment);
      setSummary(data.summary);
      setResults(data.results || []);
      setTotalRecords(data.total || 0);
    } catch {
      setAssignment(null);
      setResults([]);
    }
    setLoading(false);
  }, [id, filters, page, pageSize]);

  useEffect(() => { fetchResults(); }, [fetchResults]);
  useEffect(() => { setPage(1); }, [filters]);

  // Full assignment (description + populated question pool) — fetched once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/assignments/${id}`);
        if (!cancelled) setDetail(data.assignment || null);
      } catch {
        if (!cancelled) setDetail(null);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const handleSearchBlur = () => {
    if (searchInput !== search) setSearch(searchInput);
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const toggleExpand = (studentId) => {
    setExpandedRow(prev => prev === studentId ? null : studentId);
  };

  const columns = [
    {
      key: "expand",
      label: "",
      width: "40px",
      render: (r) => (
        <button
          className="asd-expand-btn"
          onClick={(e) => { e.stopPropagation(); toggleExpand(r.student._id); }}
          disabled={r.sessionStatus === "pending"}
        >
          {expandedRow === r.student._id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ),
    },
    {
      key: "index",
      label: "#",
      width: "45px",
      render: (_, index) => (page - 1) * pageSize + index + 1,
    },
    {
      key: "student",
      label: "Student",
      width: "22%",
      render: (r) => (
        <div className="asd-student-cell">
          <img
            src={`https://api.dicebear.com/7.x/micah/svg?seed=${encodeURIComponent(r.student.name)}`}
            alt=""
            className="asd-avatar"
          />
          <div>
            <div className="fw-medium">{r.student.name}</div>
            <div className="asd-student-email">{r.student.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "10%",
      render: (r) => {
        const s = statusStyle[r.sessionStatus] || statusStyle.pending;
        return <AppBadge variant={s.variant}>{s.label}</AppBadge>;
      },
    },
    {
      key: "score",
      label: "Score",
      width: "12%",
      render: (r) => {
        if (r.sessionStatus !== "submitted") return <span className="text-muted">—</span>;
        const scoreVal = r.scoreOutOf100 ?? (r.totalTests > 0 ? Math.round((r.totalPassed / r.totalTests) * 100) : 0);
        const allPassed = scoreVal === 100;
        return (
          <div className="asd-score-cell">
            <span className={`asd-score-100 ${allPassed ? "asd-score-100--perfect" : scoreVal >= 50 ? "asd-score-100--good" : "asd-score-100--low"}`}>
              {scoreVal}<span className="asd-score-100-max">/100</span>
            </span>
            <span className="asd-score-tc-sub">{r.totalPassed}/{r.totalTests} test cases</span>
          </div>
        );
      },
    },
    {
      key: "testCases",
      label: "Questions",
      width: "10%",
      render: (r) => {
        if (r.sessionStatus !== "submitted" || !r.totalQuestions) return <span className="text-muted">—</span>;
        const allPassed = r.score === r.totalQuestions;
        return (
          <span className={`asd-score ${allPassed ? "asd-score--pass" : "asd-score--partial"}`}>
            {r.score}/{r.totalQuestions} passed
          </span>
        );
      },
    },
    {
      key: "startedAt",
      label: "Started",
      width: "14%",
      render: (r) => r.startedAt ? formatToReadableDateTime(r.startedAt) : "—",
    },
    {
      key: "submittedAt",
      label: "Submitted",
      width: "14%",
      render: (r) => r.submittedAt ? formatToReadableDateTime(r.submittedAt) : "—",
    },
  ];

  /* ── Expanded row renderer ── */
  const renderExpandedRow = (r) => {
    if (expandedRow !== r.student._id || !r.answers?.length) return null;

    return (
      <tr key={`exp-${r.student._id}`} className="asd-expanded-row">
        <td colSpan={columns.length}>
          <div className="asd-answers">
            {r.answers.map((ans, qi) => {
              const lang = Object.keys(ans.code || {})[0] || "—";
              const code = ans.code?.[lang] || "";
              const visible = (ans.results || []).filter(t => !t.isHidden);
              const hidden = (ans.results || []).filter(t => t.isHidden);
              const hiddenPassed = hidden.filter(t => t.passed).length;

              return (
                <div key={qi} className="asd-answer-card">
                  <div className="asd-answer-header">
                    <span className="asd-answer-q">Q{qi + 1}</span>
                    <AppBadge variant={ans.status === "passed" ? "success" : ans.status === "failed" ? "danger" : "secondary"}>
                      {ans.status}
                    </AppBadge>
                    <span className={`asd-answer-score-100 ${ans.total > 0 && ans.passedCount === ans.total ? "asd-answer-score-100--perfect" : ""}`}>
                      {ans.total > 0 ? Math.round((ans.passedCount / ans.total) * 100) : 0}/100
                    </span>
                    <span className="asd-answer-tc">
                      <CheckCircle size={12} /> {ans.passedCount}/{ans.total} test cases
                    </span>
                    {lang !== "—" && (
                      <span className="asd-answer-lang">
                        <Code2 size={12} /> {lang}
                      </span>
                    )}
                  </div>

                  {/* Test case results */}
                  {visible.length > 0 && (
                    <div className="asd-tc-grid">
                      {visible.map((tc, ti) => (
                        <div key={ti} className={`asd-tc-item ${tc.passed ? "asd-tc-item--pass" : "asd-tc-item--fail"}`}>
                          <div className="asd-tc-status">
                            {tc.passed ? <CheckCircle size={12} className="text-success" /> : <XCircle size={12} className="text-danger" />}
                            <span>Case {ti + 1}</span>
                          </div>
                          <div className="asd-tc-data">
                            <div><span className="asd-tc-label">Input</span><code>{tc.input || "(empty)"}</code></div>
                            <div><span className="asd-tc-label">Expected</span><code>{tc.expected}</code></div>
                            <div><span className="asd-tc-label">Output</span><code>{tc.output || "—"}</code></div>
                          </div>
                        </div>
                      ))}
                      {hidden.length > 0 && (
                        <div className={`asd-tc-item ${hiddenPassed === hidden.length ? "asd-tc-item--pass" : "asd-tc-item--fail"} asd-tc-item--hidden`}>
                          <div className="asd-tc-status">
                            {hiddenPassed === hidden.length ? <CheckCircle size={12} className="text-success" /> : <XCircle size={12} className="text-danger" />}
                            <span>{hiddenPassed}/{hidden.length} hidden test case{hidden.length !== 1 ? "s" : ""} passed</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Submitted code */}
                  {code && (
                    <div className="asd-code-block">
                      <div className="asd-code-header">
                        <span>Submitted Code ({lang})</span>
                        <button className="asd-copy-btn" onClick={() => handleCopyCode(code)}>
                          <Copy size={12} />
                          {copiedCode === code ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <Editor
                        height={Math.min(300, Math.max(100, code.split("\n").length * 19))}
                        language={lang}
                        value={code}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 13,
                          lineNumbers: "on",
                          folding: false,
                          contextmenu: false,
                          renderLineHighlight: "none",
                          padding: { top: 8 },
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </td>
      </tr>
    );
  };

  /* ── Questions / assignment info tab ── */
  const renderQuestionsTab = () => {
    const desc = detail?.description;
    const questions = detail?.questions || [];
    const lockedLangs = detail?.lockedLanguages || [];

    const metaItems = [
      { icon: Users, label: "Batch", value: assignment.batch || "—" },
      { icon: ClipboardList, label: "Type", value: assignment.assignmentType === "practice" ? "Practice" : "Exam" },
      { icon: ListChecks, label: "Questions per student", value: `${assignment.numQuestions} of ${questions.length || assignment.numQuestions} in pool` },
      { icon: Timer, label: "Time limit", value: assignment.timeLimit ? `${assignment.timeLimit} min` : "No limit" },
      { icon: Calendar, label: "Start", value: assignment.startDate ? formatToReadableDateTime(assignment.startDate) : "—" },
      { icon: Calendar, label: "End", value: assignment.endDate ? formatToReadableDateTime(assignment.endDate) : "—" },
    ];

    return (
      <div className="asd-info">
        {/* Meta grid */}
        <div className="asd-meta-grid">
          {metaItems.map((m, i) => (
            <div key={i} className="asd-meta-item">
              <m.icon size={15} className="asd-meta-icon" />
              <div>
                <div className="asd-meta-label">{m.label}</div>
                <div className="asd-meta-value">{m.value}</div>
              </div>
            </div>
          ))}
        </div>

        {lockedLangs.length > 0 && (
          <div className="asd-locked-langs">
            <Code2 size={14} />
            <span>Allowed languages:</span>
            {lockedLangs.map((l) => <AppBadge key={l} variant="secondary">{l}</AppBadge>)}
          </div>
        )}

        {/* Description */}
        {desc && (
          <div className="asd-desc-card">
            <div className="asd-section-title"><FileText size={15} /> Description</div>
            <div className="asd-desc-html" dangerouslySetInnerHTML={{ __html: desc }} />
          </div>
        )}

        {/* Question pool */}
        <div className="asd-section-title">
          <ListChecks size={15} /> Question Pool ({questions.length})
        </div>

        {questions.length === 0 && (
          <div className="text-muted text-center my-4">
            {detail ? "No questions in this assignment." : "Loading questions…"}
          </div>
        )}

        <div className="asd-question-list">
          {questions.map((q, qi) => {
            const langs = (q.categories || []).map((c) => c.name).filter(Boolean);
            const tcCount = (q.testCases || []).length;
            const hiddenCount = (q.testCases || []).filter((t) => t.isHidden).length;
            return (
              <div key={q._id || qi} className="asd-question-card">
                <div className="asd-question-head">
                  <span className="asd-question-num">Q{qi + 1}</span>
                  <span className="asd-question-title">{q.title}</span>
                  {q.difficulty && (
                    <span className={`asd-diff ${difficultyClass(q.difficulty)}`}>{q.difficulty}</span>
                  )}
                </div>

                {q.description && (
                  <div className="asd-question-desc" dangerouslySetInnerHTML={{ __html: q.description }} />
                )}

                <div className="asd-question-meta">
                  {tcCount > 0 && (
                    <span className="asd-question-chip">
                      <CheckCircle size={12} /> {tcCount} test case{tcCount !== 1 ? "s" : ""}
                      {hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ""}
                    </span>
                  )}
                  {langs.length > 0 && (
                    <span className="asd-question-chip">
                      <Layers size={12} /> {langs.join(", ")}
                    </span>
                  )}
                  {(q.labels || []).map((l) => (
                    <span key={l} className="asd-question-chip asd-question-chip--label">
                      <Tag size={12} /> {l}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading && !assignment) {
    return <div className="d-flex justify-content-center my-5"><Spinner animation="border" /></div>;
  }

  if (!assignment) {
    return <div className="text-muted text-center my-5">No assignment data found.</div>;
  }

  return (
    <div className="asd-page">
      <PageHeader
        backButton
        onBack={() => navigate(`${prefix}/assignments`)}
        icon={ClipboardList}
        title={assignment.title}
        description={`Batch: ${assignment.batch} · ${assignment.numQuestions} questions · ${assignment.timeLimit || "—"} min`}
      />

      <Tabs value={activeTab} onChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="questions"><ListChecks size={15} /> Questions</TabsTrigger>
          <TabsTrigger value="results"><Users size={15} /> Student Results</TabsTrigger>
        </TabsList>

        <TabsContent value="questions">
          {renderQuestionsTab()}
        </TabsContent>

        <TabsContent value="results">

      {/* Summary Stats */}
      <div className="asd-stats">
        <div className="asd-stat-card">
          <Users size={18} className="asd-stat-icon" />
          <div>
            <div className="asd-stat-value">{summary?.totalStudents || 0}</div>
            <div className="asd-stat-label">Total Students</div>
          </div>
        </div>
        <div className="asd-stat-card asd-stat-card--success">
          <CheckCircle size={18} className="asd-stat-icon" />
          <div>
            <div className="asd-stat-value">{summary?.completed || 0}</div>
            <div className="asd-stat-label">Completed</div>
          </div>
        </div>
        <div className="asd-stat-card asd-stat-card--warning">
          <Clock size={18} className="asd-stat-icon" />
          <div>
            <div className="asd-stat-value">{summary?.pending || 0}</div>
            <div className="asd-stat-label">Pending</div>
          </div>
        </div>
        <div className="asd-stat-card asd-stat-card--info">
          <Award size={18} className="asd-stat-icon" />
          <div>
            <div className="asd-stat-value">
              {summary?.completed && summary?.totalStudents
                ? Math.round((summary.completed / summary.totalStudents) * 100)
                : 0}%
            </div>
            <div className="asd-stat-label">Completion Rate</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="asl-toolbar">
        <div className="asl-search">
          <Search size={16} className="asl-search-icon" />
          <input
            type="text"
            className="asl-search-input"
            placeholder="Search students..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={(e) => e.key === "Enter" && handleSearchBlur()}
          />
        </div>

        <div className="asl-toolbar-actions">
          <FilterButton count={listFilters.count} onClick={listFilters.open} />
        </div>
      </div>

      <ActiveFilterChips items={listFilters.activeFilters} onClearAll={listFilters.reset} />

      <FilterPanel {...listFilters.panelProps} title="Filter Results">
        <FilterSelectField {...listFilters.selectProps("status")} />
      </FilterPanel>

      <DataTable
        data={results}
        columns={columns}
        loading={loading}
        hoverable
        pagination={{
          page,
          pageSize,
          total: totalRecords,
          onPageChange: setPage,
        }}
        emptyMessage="No student results found"
        renderExpandedRow={renderExpandedRow}
      />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AssignmentDetails;
